import type { ReactNode } from 'react';
import { InteractiveSurface } from '@viquard/doc-ui/components';
import { IcnLink } from '@viquard/doc-ui/icons';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import { resolveKind } from '../utils/kindTheme.js';
import { localTime } from '../utils/dateUtils.js';
import { EventLinks } from './EventLinks.js';
import { RecurrenceBadge } from './RecurrenceBadge.js';
import { ReminderBadge } from './ReminderBadge.js';
import { StatusBadge } from './StatusBadge.js';
import { useEventContextMenu } from '../hooks/useEventContextMenu.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { formatMessage, useCalendarMessages } from '../i18n.js';
import { formatEventLocation } from '../utils/location.js';

export interface EventCardProps {
  readonly event: CalendarEvent;
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly onClick?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  /** Slot for consumer-injected extras (e.g. AttendanceRoster). Renders
   *  inside the card after the foot. */
  readonly extras?: ReactNode | undefined;
  readonly density?: 'comfortable' | 'compact' | undefined;
  readonly selected?: boolean | undefined;
  /** Viewer TZ for time projection. */
  readonly viewerTz?: string | undefined;
  /** Right-click context menu actions (Open / Edit / Cancel / Delete …). */
  readonly contextMenu?: EventMenuActions | undefined;
}

/** Detail-rich event card used in side rails / agenda lists / day view.
 *  Shows: kind badge, optional group dot, title, time, meta, links,
 *  cross-post info, status badge. */
export function EventCard(props: EventCardProps): ReactNode {
  const {
    event,
    kindTheme,
    groups,
    onClick,
    onSelectLink,
    extras,
    density = 'comfortable',
    selected = false,
    viewerTz,
    contextMenu,
  } = props;
  const messages = useCalendarMessages();
  const cm = useEventContextMenu(event, contextMenu ?? {}, {
    disabled: contextMenu === undefined,
    ariaLabel: messages.eventActionsAriaLabel,
  });
  const kind = resolveKind(kindTheme, event.kind);
  const time = localTime(event.startsAt, viewerTz);
  const group = groups?.find((g) => g.id === event.groupId);
  const Icon = kind.icon;
  const locationLabel = formatEventLocation(event, messages);

  // Resolve cross-post group names for the footer line.
  const crossPostNames =
    event.crossPostedTo !== undefined && event.crossPostedTo.length > 0 && groups !== undefined
      ? event.crossPostedTo
          .map((gid) => groups.find((g) => g.id === gid)?.name)
          .filter((n): n is string => n !== undefined)
      : [];

  // The card has interactive button descendants (EventLinks chips +
  // optional render-extras buttons), so the card itself must NOT be a
  // button. InteractiveSurface keeps keyboard / a11y behavior in doc-ui.
  const interactive = onClick !== undefined;
  const className = [
    'vq-cal__card',
    density === 'compact' ? 'is-compact' : '',
    event.status === 'cancelled' ? 'is-cancelled' : '',
    selected ? 'is-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const style = { ['--vq-cal-kind' as string]: kind.color };
  const content = (
    <>
      <div className="vq-cal__card-eyebrow">
        <span className="vq-cal__card-kind">
          {Icon !== undefined && <Icon size={12} />}
          <span>{kind.label}</span>
        </span>
        <RecurrenceBadge event={event} />
        <ReminderBadge event={event} />
        {group !== undefined && (
          <span className="vq-cal__card-group">
            <span
              className="vq-cal__card-group-dot"
              style={group.color !== undefined ? { background: group.color } : undefined}
              aria-hidden="true"
            />
            <span>{group.name}</span>
          </span>
        )}
        {event.status !== undefined && event.status !== 'scheduled' && (
          <StatusBadge status={event.status} />
        )}
      </div>
      <h3 className="vq-cal__card-title">{event.title}</h3>
      {(time !== null || locationLabel !== null || event.meta !== undefined) && (
        <div className="vq-cal__card-foot">
          {time !== null && <span className="vq-cal__card-time">{time}</span>}
          {locationLabel !== null && (
            <span className="vq-cal__card-location">{locationLabel}</span>
          )}
          {event.meta !== undefined && <span className="vq-cal__card-meta">{event.meta}</span>}
        </div>
      )}
      <EventLinks event={event} onSelectLink={onSelectLink} density={density} />
      {crossPostNames.length > 0 && (
        <div className="vq-cal__card-cross">
          <IcnLink size={11} />
          <span>
            {formatMessage(messages.eventCrossPostedTo, {
              groups: crossPostNames.join(' · '),
            })}
          </span>
        </div>
      )}
      {extras !== undefined && <div className="vq-cal__card-extras">{extras}</div>}
    </>
  );

  return (
    <>
      {interactive ? (
        <InteractiveSurface
          className={className}
          style={style}
          onClick={() => { onClick(event); }}
          onContextMenu={cm.bind.onContextMenu}
          aria-pressed={selected}
        >
          {content}
        </InteractiveSurface>
      ) : (
        <div className={className} style={style} onContextMenu={cm.bind.onContextMenu}>
          {content}
        </div>
      )}
      {cm.menu}
    </>
  );
}
